// biometricAuthService.ts
import axios from 'axios';
import type { AxiosResponse, AxiosError } from 'axios';
import ENV from '@/lib/env';
import { logger } from '@/lib/logger';
import { fetchAuthSession } from 'aws-amplify/auth';

// Configuration
// In local dev, route all biometric calls through the Vite proxy (/bio-proxy → vtx3qf6sfd)
// This bypasses CORS since the proxy makes server-to-server requests.
// In production, use the real biometric API URL.
const isLocalDev = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const API_BASE_URL = isLocalDev
  ? '/bio-proxy'
  : ENV.FACE_BIOMETRIC_API_BASE_URL;
const DEFAULT_TIMEOUT = 10 * 60 * 1000; // 10 minutes

// Type Definitions
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: ApiError;
  headers?: any;
}

export interface ApiError {
  message: string;
  status: number;
  code: string;
  details?: any;
}

export interface TranscriptionResponse {
  success: boolean;
  text?: string;
  transcription?: {
    spanish: boolean;
    spanish_full_text: string;
    english: {
      full_text: string;
      segments: Array<{
        start: number;
        end: number;
        text: string;
      }>;
    };
  };
  audioInfo?: {
    detected_language: string;
    duration_seconds: number;
    language_probability: number;
    segment_count: number;
    spanish_detected: boolean;
  };
  fileInfo?: {
    filename: string;
    size_bytes: number;
    url?: string;
    s3Key?: string;
  };
  error?: string;
}

export interface FaceMatch {
  uuid: string;
  first_name: string;
  middle_name?: string | null;
  last_name: string;
  confidence: number;
  external_image_id: string;
}

export interface FaceData {
  uuid: string;
  first_name: string;
  middle_name?: string | null;
  last_name: string;
  external_image_id: string;
  face_id: string;
}

export interface AccessoryDetectionResult {
  status: 'success' | 'warning' | 'error';
  message: string;
  details?: {
    eyewear_detected?: boolean;
    eyewear_confidence?: number;
    sunglasses_detected?: boolean;
    sunglasses_confidence?: number;
  };
  frames_processed?: number;
}

export interface AddFaceResult {
  status: 'success' | 'error';
  message?: string;
  result?: {
    face_id: string;
    external_image_id: string;
    confidence?: number;
  };
  existing_uuid?: string;
}

export interface SearchFaceResult {
  status: 'success' | 'failed' | 'error';
  message?: string;
  matches?: FaceMatch[];
}

export interface ListFacesResult {
  status: 'success' | 'error';
  message?: string;
  matches?: FaceData[];
}

export interface BiometricLoginResult {
  status: 'success' | 'failed' | 'error';
  message?: string;
  user?: {
    uuid: string;
    first_name: string;
    middle_name?: string | null;
    last_name: string;
    email?: string;
    phone?: string;
  };
  matches?: FaceMatch[];
}

export interface UpdateFaceResult {
  status: 'success' | 'error';
  message?: string;
  result?: {
    face_id: string;
    external_image_id: string;
  };
}

export interface DeleteFaceResult {
  status: 'success' | 'failed' | 'error';
  message?: string;
}

export interface BetaRegistrationResult {
  status: 'success' | 'error';
  message: string;
  one_time_code?: string;
  expires_in?: string;
}

export interface BetaVerificationResult {
  status: 'success' | 'error';
  message: string;
}

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

export interface MergeVideosResult {
  status: 'success' | 'error';
  message?: string;
  code?: string;
}

/**
 * Parameters for transcribeAudio — all required by POST /transcribe-and-save
 */
export interface TranscribeAudioParams {
  audioFile: File;
  branch: string;          // branch UUID
  hospital_name: string;   // hospital display name
  uuid: string;            // patient UUID
  survey_reference: string; // survey/questionnaire reference ID
  access_code?: string;    // optional: unique_page_id for patient SMS flow (no JWT needed)
}

/**
 * Biometric Authentication Service
 * Handles all biometric-related API calls to the Flask backend
 */
class BiometricAuthService {

  /**
   * Get or refresh authentication token from AWS Cognito
   */
  private async getValidToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) {
        console.error('No ID token found in Amplify session');
        return null;
      }
      return idToken;
    } catch (error) {
      console.error('Error getting Cognito token:', error);
      return null;
    }
  }

  /**
   * Get the correct token for biometric (RDS) operations.
   * Prioritizes the backend JWT token (admin token) over Cognito.
   */
  public async getBiometricToken(): Promise<string | null> {
    const storedToken = localStorage.getItem('jwt_token');
    if (storedToken && this.isTokenValid(storedToken)) {
      return storedToken;
    }
    console.log('No valid JWT token found, falling back to Cognito token...');
    return await this.getValidToken();
  }

  /**
   * Check if token is valid and not expired
   */
  private isTokenValid(token: string): boolean {
    if (!token) return false;
    try {
      const expiryTime = localStorage.getItem('jwt_token_expiry');
      if (expiryTime) {
        const expiry = parseInt(expiryTime);
        if (expiry - Date.now() < 5 * 60 * 1000) {
          console.log('Token expires soon, treating as invalid');
          return false;
        }
      }
      const payload = this.decodeJWT(token);
      if (payload?.exp) {
        if (payload.exp * 1000 - Date.now() < 5 * 60 * 1000) {
          console.log('JWT token expires soon or is expired');
          return false;
        }
      }
      return true;
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  }

  /**
   * Decode JWT token to check expiry
   */
  private decodeJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }

  /**
   * Transcribe an audio file and save it to S3.
   *
   * Endpoint: POST /transcribe-and-save
   * S3 key structure: {uuid}/{date}/{survey_reference}/{filename}
   *
   * @param params - { audioFile, branch, hospital_name, uuid, survey_reference }
   */
  async transcribeAudio(params: TranscribeAudioParams): Promise<TranscriptionResponse> {
    const { audioFile, branch, hospital_name, uuid, survey_reference, access_code } = params;

    // Patient SMS flow: uses access_code (no JWT needed).
    // Clinical staff flow: uses JWT Bearer token.
    const isPatientFlow = !!access_code;

    // Only fetch JWT token for clinical staff flow
    let token: string | null = null;
    if (!isPatientFlow) {
      token = await this.getBiometricToken();
      if (!token) {
        return { success: false, error: 'Authentication failed. Please try again.' };
      }
    }

    const formData = new FormData();
    formData.append('audio', audioFile, audioFile.name || 'audio.mp3');
    formData.append('branch', branch);
    formData.append('hospital_name', hospital_name);
    formData.append('uuid', uuid);
    formData.append('survey_reference', survey_reference);
    if (access_code) {
      formData.append('access_code', access_code);
    }

    try {
      logger.debug('📤 Sending transcribe-and-save request:', {
        filename: audioFile.name,
        size: `${(audioFile.size / 1024 / 1024).toFixed(2)}MB`,
        branch,
        survey_reference,
      });

      const buildHeaders = (tkn: string | null) => ({
        ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}),
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      });

      let response;
      try {
        response = await axios.post(`${API_BASE_URL}/transcribe-and-save`, formData, {
          headers: buildHeaders(token),
          timeout: DEFAULT_TIMEOUT,
        });
      } catch (err) {
        // 401 → refresh token and retry once (only for JWT flow)
        if (axios.isAxiosError(err) && err.response?.status === 401 && !isPatientFlow) {
          console.log('🔄 Token expired, refreshing...');
          localStorage.removeItem('jwt_token');
          localStorage.removeItem('jwt_token_expiry');
          const newToken = await this.getBiometricToken();
          if (!newToken) {
            return { success: false, error: 'Failed to refresh authentication. Please try again.' };
          }
          response = await axios.post(`${API_BASE_URL}/transcribe-and-save`, formData, {
            headers: buildHeaders(newToken),
            timeout: DEFAULT_TIMEOUT,
          });
        } else {
          throw err;
        }
      }

      logger.debug('✅ transcribe-and-save completed successfully');

      const isSuccess = response.data.status === 'success' || response.data.success === true;
      
      return {
        success: isSuccess,
        text: response.data.transcription?.english?.full_text || response.data.transcription?.text || response.data.text,
        error: !isSuccess ? (response.data.message || response.data.error || 'Transcription failed') : undefined,
        transcription: response.data.transcription,
        audioInfo: response.data.audio_info || response.data.audioInfo,
        fileInfo: {
          ...(response.data.file_info || response.data.fileInfo),
          s3Key: response.data.s3_info?.s3_key,
        },
      };
    } catch (error) {
      console.error('❌ Transcription error:', error);
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          return { success: false, error: 'Authentication expired. Please refresh and try again.' };
        }
        if (error.response?.status === 400) {
          // Backend returned a specific validation error (e.g. unsupported language).
          // Surface the exact message so the user knows what went wrong.
          const msg = error.response?.data?.message || error.response?.data?.error || 'Audio could not be processed. Please type your response instead.';
          return { success: false, error: msg };
        }
        if (error.response?.status === 413) {
          return { success: false, error: 'Audio file too large. Please try a shorter recording.' };
        }
        if (error.response?.status === 503 || error.response?.status === 502 || error.response?.status === 504) {
          return { success: false, error: 'Audio transcription is temporarily unavailable. Please type your response manually.' };
        }
        if (error.code === 'ECONNABORTED') {
          return { success: false, error: 'Request timeout. The audio may be too long. Please type your response instead.' };
        }
        if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
          return { success: false, error: 'Unable to reach the transcription service. Please check your connection.' };
        }
        return { success: false, error: error.response?.data?.message || error.message };
      }
      return { success: false, error: 'An unknown error occurred during audio transcription' };
    }
  }

  /**
   * Clear authentication data (useful for logout)
   */
  clearAuthData(): void {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('jwt_token_expiry');
  }

  /**
   * Check if user needs to re-authenticate
   */
  needsReauth(): boolean {
    const token = localStorage.getItem('jwt_token');
    return !token || !this.isTokenValid(token);
  }

  /**
   * Login to get JWT token
   */
  async login(uuid: string, staff_login: boolean): Promise<ApiResponse<{ access_token: string }>> {
    try {
      const rdsBaseUrl = ENV.VITE_AWS_API_GATEWAY_URL;
      const cleanBaseUrl = rdsBaseUrl.replace(/\/$/, '');
      const loginUrl = `${cleanBaseUrl}/auth/login`;
      logger.debug('Biometric login requested', { staff_login });

      const response = await axios.post(loginUrl, { uuid, staff_login }, {
        headers: { 'Content-Type': 'application/json' },
        timeout: DEFAULT_TIMEOUT,
      });

      if (response.data.access_token) {
        localStorage.setItem('jwt_token', response.data.access_token);
        localStorage.setItem('jwt_token_expiry', (Date.now() + 24 * 60 * 60 * 1000).toString());
      }

      return { success: true, data: response.data };
    } catch (error) {
      console.error('Login error:', error);
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND') {
          return { success: false, error: { message: 'Biometric API server is not available', status: 503, code: 'SERVICE_UNAVAILABLE' } };
        }
        if (error.response?.status === 404) {
          return { success: false, error: { message: 'Biometric API endpoint not found', status: 404, code: 'ENDPOINT_NOT_FOUND' } };
        }
      }
      return this.handleError(error as AxiosError, 'Failed to login');
    }
  }

  /**
   * Detect accessories (eyewear) in uploaded video/image
   */
  async detectAccessories(file: File): Promise<ApiResponse<AccessoryDetectionResult>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = await this.getBiometricToken();
      if (!token) return { success: false, error: { message: 'Authentication failed', status: 401, code: 'AUTH_ERROR' } };

      const response: AxiosResponse<AccessoryDetectionResult> = await axios.post(
        `${API_BASE_URL}/detect-accessories`, formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }, timeout: DEFAULT_TIMEOUT }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return this.handleError(error as AxiosError, 'Failed to detect accessories');
    }
  }

  /**
   * Add a new face to the collection
   */
  async addFace(file: File, firstName: string, patient_uuid: string, lastName: string): Promise<ApiResponse<AddFaceResult>> {
    try {
      const formData = new FormData();
      formData.append('uuid', patient_uuid.trim());
      formData.append('first_name', firstName.trim());
      formData.append('last_name', lastName.trim());
      formData.append('file', file, file.name);

      const token = await this.getBiometricToken();
      if (!token) return { success: false, error: { message: 'Authentication failed', status: 401, code: 'AUTH_ERROR' } };

      const response = await axios.post<AddFaceResult>(`${API_BASE_URL}/add-face`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}`, Accept: 'application/json' },
        timeout: 60000,
        maxBodyLength: 100 * 1024 * 1024,
        maxContentLength: 100 * 1024 * 1024,
      });
      return { success: true, data: response.data };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return { success: false, error: { message: error.response?.data?.message || 'Failed to add face', status: error.response?.status || 500, code: 'API_ERROR', details: error.response?.data } };
      }
      return this.handleError(error as AxiosError, 'An unexpected error occurred while adding face');
    }
  }

  /**
   * Search for a face in the collection
   */
  async searchFace(imageFile: File): Promise<ApiResponse<SearchFaceResult>> {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      const token = await this.getBiometricToken();
      if (!token) return { success: false, error: { message: 'Authentication failed', status: 401, code: 'AUTH_ERROR' } };

      const response: AxiosResponse<SearchFaceResult> = await axios.post(
        `${API_BASE_URL}/search-face`, formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data', Accept: 'application/json' }, timeout: DEFAULT_TIMEOUT }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return this.handleError(error as AxiosError, 'Failed to search face');
    }
  }

  /**
   * Biometric login using face recognition
   */
  async biometricLogin(imageFile: File, uuid?: string): Promise<ApiResponse<BiometricLoginResult>> {
    const apiEndpoint = `${API_BASE_URL}/biometric-login`;
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      if (uuid) {
        formData.append('uuid', uuid.trim());
      }

      // /biometric-login does NOT require a JWT on the backend — it is a public route
      // used by both clinical staff (who have a token) and SMS patients (who have none).
      // We include the token when available, but never block if it's absent.
      const token = await this.getBiometricToken().catch(() => null);
      const headers: Record<string, string> = {
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await axios.post<BiometricLoginResult & { token?: string; expires_in?: number }>(
        apiEndpoint, formData,
        { timeout: DEFAULT_TIMEOUT, headers }
      );

      if (response.data.token && response.data.expires_in) {
        const expiryTime = Date.now() + response.data.expires_in * 1000;
        localStorage.setItem('jwt_token', response.data.token);
        localStorage.setItem('jwt_token_expiry', expiryTime.toString());
      }
      return { success: true, data: response.data };
    } catch (error) {
      return this.handleError(error as AxiosError, 'Biometric authentication failed');
    }
  }

  /**
   * List all faces in the collection
   */
  async listFaces(collectionId?: string): Promise<ApiResponse<ListFacesResult>> {
    try {
      const token = await this.getBiometricToken();
      if (!token) return { success: false, error: { message: 'Authentication failed', status: 401, code: 'AUTH_ERROR' } };

      const response: AxiosResponse<ListFacesResult> = await axios.get(`${API_BASE_URL}/list-faces`, {
        params: collectionId ? { collection_id: collectionId } : {},
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        timeout: DEFAULT_TIMEOUT,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return this.handleError(error as AxiosError, 'Failed to list faces');
    }
  }

  /**
   * Update an existing face in the collection
   */
  async updateFace(imageFile: File, uuid: string, firstName: string, lastName: string): Promise<ApiResponse<UpdateFaceResult>> {
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      formData.append('uuid', uuid.trim());
      formData.append('first_name', firstName.trim());
      formData.append('last_name', lastName.trim());
      const token = await this.getBiometricToken();
      if (!token) return { success: false, error: { message: 'Authentication failed', status: 401, code: 'AUTH_ERROR' } };

      const response: AxiosResponse<UpdateFaceResult> = await axios.post(
        `${API_BASE_URL}/update-face`, formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data', Accept: 'application/json' }, timeout: DEFAULT_TIMEOUT }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return this.handleError(error as AxiosError, 'Failed to update face');
    }
  }



  /**
   * Delete a face from the collection (Admin only)
   */
  async deleteFace(externalImageId: string): Promise<ApiResponse<DeleteFaceResult>> {
    try {
      const formData = new FormData();
      formData.append('external_image_id', externalImageId);
      const token = await this.getBiometricToken();
      if (!token) return { success: false, error: { message: 'Admin authentication failed', status: 401, code: 'AUTH_ERROR' } };

      const response = await axios.post<DeleteFaceResult>(`${API_BASE_URL}/delete-face`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data', Accept: 'application/json' },
        timeout: DEFAULT_TIMEOUT,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return this.handleError(error as AxiosError, 'Failed to delete face');
    }
  }

  /**
   * Delete a face by user components (Admin only)
   */
  async deleteFaceByComponents(uuid: string, firstName: string, lastName: string): Promise<ApiResponse<DeleteFaceResult>> {
    try {
      const formData = new FormData();
      formData.append('uuid', uuid.trim());
      formData.append('first_name', firstName.trim());
      formData.append('last_name', lastName.trim());
      const token = await this.getBiometricToken();
      if (!token) return { success: false, error: { message: 'Authentication failed', status: 401, code: 'AUTH_ERROR' } };

      const response: AxiosResponse<DeleteFaceResult> = await axios.post(
        `${API_BASE_URL}/delete-face`, formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data', Accept: 'application/json' }, timeout: DEFAULT_TIMEOUT }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return this.handleError(error as AxiosError, 'Failed to delete face');
    }
  }

  /**
   * Merge multiple videos into a single video
   */
  async mergeVideos(videoFiles: File[]): Promise<ApiResponse<Blob>> {
    try {
      if (!Array.isArray(videoFiles) || videoFiles.length < 2) throw new Error('At least two video files are required');
      const formData = new FormData();
      videoFiles.forEach((file, index) => formData.append(`video${index}`, file));
      const token = await this.getBiometricToken();
      if (!token) return { success: false, error: { message: 'Authentication failed', status: 401, code: 'AUTH_ERROR' } };

      const response: AxiosResponse<Blob> = await axios.post(`${API_BASE_URL}/merge-videos`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data', Accept: 'application/json' },
        responseType: 'blob',
        timeout: DEFAULT_TIMEOUT,
      });
      return { success: true, data: response.data, headers: response.headers };
    } catch (error) {
      return this.handleError(error as AxiosError, 'Failed to merge videos');
    }
  }

  /**
   * Add face with video merging
   */
  async addFaceWithMerge(videoFiles: File[], uuid: string, firstName: string, lastName: string): Promise<ApiResponse<AddFaceResult>> {
    try {
      if (!Array.isArray(videoFiles) || videoFiles.length < 2) throw new Error('At least two video files are required');
      const formData = new FormData();
      videoFiles.forEach((file, index) => formData.append(`video${index}`, file));
      formData.append('uuid', uuid.trim());
      formData.append('first_name', firstName.trim());
      formData.append('last_name', lastName.trim());
      const token = await this.getBiometricToken();
      if (!token) return { success: false, error: { message: 'Authentication failed', status: 401, code: 'AUTH_ERROR' } };

      const response: AxiosResponse<AddFaceResult> = await axios.post(`${API_BASE_URL}/add-face-with-merge`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data', Accept: 'application/json' },
        timeout: DEFAULT_TIMEOUT,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return this.handleError(error as AxiosError, 'Failed to add face with video merge');
    }
  }

  /**
   * Register beta tester
   */
  async registerBetaTester(emailId: string, isAndroid = false): Promise<ApiResponse<BetaRegistrationResult>> {
    try {
      const response: AxiosResponse<BetaRegistrationResult> = await axios.post(
        `${API_BASE_URL}/register-beta`,
        { email_id: emailId, is_android: isAndroid },
        { headers: { 'Content-Type': 'application/json' }, timeout: DEFAULT_TIMEOUT }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return this.handleError(error as AxiosError, 'Failed to register beta tester');
    }
  }

  /**
   * Verify beta tester OTP
   */
  async verifyBetaOtp(emailId: string, oneTimeCode: string): Promise<ApiResponse<BetaVerificationResult>> {
    try {
      const response: AxiosResponse<BetaVerificationResult> = await axios.post(
        `${API_BASE_URL}/verify-beta-otp`,
        { email_id: emailId, one_time_code: oneTimeCode },
        { headers: { 'Content-Type': 'application/json' }, timeout: DEFAULT_TIMEOUT }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return this.handleError(error as AxiosError, 'Failed to verify OTP');
    }
  }

  /** Validate file type and size */
  validateFile(
    file: File,
    allowedTypes: string[] = ['video/mp4', 'video/mov', 'video/avi', 'image/jpeg', 'image/png'],
    maxSizeMB = 50
  ): FileValidationResult {
    if (!file) return { isValid: false, error: 'No file provided' };
    if (!allowedTypes.includes(file.type)) return { isValid: false, error: `Invalid file type. Allowed: ${allowedTypes.join(', ')}` };
    if (file.size > maxSizeMB * 1024 * 1024) return { isValid: false, error: `File size exceeds ${maxSizeMB}MB limit` };
    return { isValid: true };
  }

  createVideoPreviewUrl(file: File): string { return URL.createObjectURL(file); }
  revokeVideoPreviewUrl(url: string): void { URL.revokeObjectURL(url); }

  downloadVideo(videoBlob: Blob, filename = 'merged_video.mp4'): void {
    const url = URL.createObjectURL(videoBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private handleError(error: AxiosError, defaultMessage: string): ApiResponse {
    console.error('BiometricAuthService Error:', error);
    if (error.response) {
      const { data, status } = error.response;
      const responseData = data as any;
      return { success: false, error: { message: responseData?.message || defaultMessage, status, code: responseData?.code || 'API_ERROR', details: responseData?.details || null } };
    } else if (error.request) {
      return { success: false, error: { message: 'Network error. Please check your connection.', status: 0, code: 'NETWORK_ERROR' } };
    }
    return { success: false, error: { message: error.message || defaultMessage, status: 0, code: 'UNKNOWN_ERROR' } };
  }
}

export default new BiometricAuthService();